import { normalizeYoutubeVideos } from './youtube';

const IMAGE_LINE =
  /^IMAGE\s+url="([^"]*)"(?:\s+alt="([^"]*)")?(?:\s+description="([^"]*)")?\s*$/i;

function unescapeAttr(value) {
  return String(value || '').replace(/&quot;/g, '"');
}

function escapeAttr(value) {
  return String(value || '').replace(/"/g, '&quot;');
}

export function emptyLectureImage(url = '', alt = '', description = '') {
  return {
    id: newLectureBlockId(),
    imageUrl: url,
    imagePath: '',
    alt,
    description,
  };
}

export function sectionImages(block) {
  if (!block) return [];
  if (Array.isArray(block.images) && block.images.length) {
    return block.images.filter((img) => img?.imageUrl || img?.url);
  }
  if (block.type === 'image' && (block.imageUrl || block.url)) {
    return [block];
  }
  return [];
}

function isImageItem(item) {
  if (!item || typeof item !== 'object') return false;
  const t = String(item.type || '').toLowerCase();
  if (t === 'image' || t === 'figure' || t === 'photo') return true;
  if (t === 'section' || t === 'text' || t === 'heading') return false;
  const hasSection = Boolean(item.heading || item.title || item.body || item.text);
  const hasUrl = Boolean(item.imageUrl || item.url || item.src || typeof item.image === 'string');
  return hasUrl && !hasSection;
}

export function lectureImagesExplainText(dayOrBlocks = {}) {
  const blocks = Array.isArray(dayOrBlocks)
    ? dayOrBlocks
    : Array.isArray(dayOrBlocks.contentBlocks)
      ? dayOrBlocks.contentBlocks
      : [];
  const lines = [];
  let n = 0;
  blocks.forEach((block) => {
    const heading = String(block.heading || '').trim();
    sectionImages(block).forEach((img) => {
      n += 1;
      const desc = String(img.description || img.alt || '').trim();
      const where = heading ? ` (section: ${heading})` : '';
      lines.push(
        desc
          ? `Lecture figure ${n}${where}:\n${desc}`
          : `Lecture figure ${n}${where} is shown in the lesson at this position.`
      );
    });
  });
  return lines.join('\n\n');
}

export function newLectureBlockId() {
  return `b_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyLectureSection(heading = 'Introduction', text = '') {
  return {
    id: newLectureBlockId(),
    type: 'section',
    heading,
    text,
    images: [],
  };
}

function normalizeAttachedImage(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') {
    return raw ? emptyLectureImage(raw) : null;
  }
  const url = String(raw.imageUrl || raw.url || raw.src || (typeof raw.image === 'string' ? raw.image : '') || '');
  if (!url) return null;
  return {
    id: raw.id || newLectureBlockId(),
    imageUrl: url,
    imagePath: String(raw.imagePath || raw.path || ''),
    alt: String(raw.alt || raw.caption || ''),
    description: String(raw.description || raw.explain || raw.caption || raw.alt || ''),
  };
}

function attachedImagesFromRaw(raw) {
  if (!raw || typeof raw !== 'object') return [];
  const list = [];
  if (Array.isArray(raw.images)) {
    raw.images.forEach((img) => {
      const n = normalizeAttachedImage(img);
      if (n) list.push(n);
    });
  }
  const single = normalizeAttachedImage(raw.image && typeof raw.image === 'object' ? raw.image : null);
  if (single) list.push(single);
  return list;
}

function blockHasBody(block) {
  if (!block) return false;
  if (sectionImages(block).length) return true;
  if (block.type === 'image') return Boolean(block.imageUrl);
  return Boolean(String(block.text || '').trim());
}

export function blocksHaveStudentContent(blocks) {
  return (blocks || []).some(blockHasBody);
}

export function hasAuthoredLecture(day = {}) {
  if (blocksHaveStudentContent(day.contentBlocks)) return true;
  const content = String(day.content || '').trim();
  if (!content) return false;
  if (/\[\[IMAGE\s+url=/i.test(content)) return true;
  return Boolean(content.replace(/\[\[.*?\]\]/g, '').trim());
}

export function normalizeLectureBlock(raw, index = 0) {
  if (!raw || typeof raw !== 'object') {
    return emptyLectureSection(`Section ${index + 1}`);
  }
  if (isImageItem(raw)) {
    const img = normalizeAttachedImage(raw);
    return {
      id: raw.id || img?.id || newLectureBlockId(),
      type: 'image',
      imageUrl: img?.imageUrl || '',
      imagePath: img?.imagePath || '',
      alt: img?.alt || '',
      description: img?.description || '',
    };
  }
  return {
    id: raw.id || newLectureBlockId(),
    type: 'section',
    heading: String(raw.heading || raw.title || 'Section'),
    text: String(raw.text || raw.body || raw.content || ''),
    images: attachedImagesFromRaw(raw),
  };
}

/** Merge standalone image blocks into the previous section so figures sit in a row under that section. */
export function foldLectureBlocks(blocks) {
  const out = [];
  (blocks || []).forEach((raw, i) => {
    const n = normalizeLectureBlock(raw, i);
    if (n.type === 'image') {
      const img = normalizeAttachedImage(n);
      if (!img) return;
      if (!out.length) {
        out.push({ ...emptyLectureSection('Figures'), images: [img] });
        return;
      }
      const last = out[out.length - 1];
      last.images = [...(last.images || []), img];
      return;
    }
    out.push({
      ...n,
      images: n.images && n.images.length ? n.images : attachedImagesFromRaw(raw),
    });
  });
  return out.length ? out : [emptyLectureSection()];
}

export function parseAuthoredString(content) {
  const text = String(content || '').trim();
  if (!text) return [emptyLectureSection()];

  const parts = text.split(/\[\[([\s\S]*?)\]\]/g);
  const blocks = [];
  if (parts[0] && parts[0].trim()) {
    blocks.push(emptyLectureSection('Introduction', parts[0].trim()));
  }
  for (let i = 1; i < parts.length; i += 2) {
    const marker = (parts[i] || '').trim();
    const body = (parts[i + 1] || '').trim();
    const imageMatch = marker.match(IMAGE_LINE);
    if (imageMatch) {
      blocks.push({
        id: newLectureBlockId(),
        type: 'image',
        imageUrl: unescapeAttr(imageMatch[1]),
        imagePath: '',
        alt: unescapeAttr(imageMatch[2] || ''),
        description: unescapeAttr(imageMatch[3] || ''),
      });
      if (body) {
        blocks.push(emptyLectureSection('Notes', body));
      }
      continue;
    }
    blocks.push({
      id: newLectureBlockId(),
      type: 'section',
      heading: marker || 'Section',
      text: body,
    });
  }
  return blocks.length ? blocks : [emptyLectureSection()];
}

export function blocksFromDay(day = {}) {
  if (Array.isArray(day.contentBlocks) && day.contentBlocks.length) {
    return foldLectureBlocks(day.contentBlocks);
  }
  if (day.content) return foldLectureBlocks(parseAuthoredString(day.content));
  return [emptyLectureSection(day.isRevisionLecture ? 'Revision Warm-Up' : 'Introduction')];
}

function serializeImageMarker(img) {
  const url = escapeAttr(img.imageUrl || img.url || '');
  const alt = escapeAttr(img.alt || '');
  const description = escapeAttr(img.description || '');
  return `[[IMAGE url="${url}" alt="${alt}" description="${description}"]]`;
}

export function serializeLectureBlocks(blocks) {
  return (blocks || [])
    .map((block) => {
      if (block.type === 'image') {
        return serializeImageMarker(block);
      }
      const heading = String(block.heading || 'Section').trim() || 'Section';
      const text = String(block.text || '').trim();
      const head = text ? `[[${heading}]]\n${text}` : `[[${heading}]]`;
      const imgs = sectionImages(block).map(serializeImageMarker).join('\n\n');
      return imgs ? `${head}\n\n${imgs}` : head;
    })
    .join('\n\n')
    .trim();
}

function sectionFromUnknown(item) {
  if (typeof item === 'string') {
    return [emptyLectureSection('Section', item)];
  }
  if (isImageItem(item)) {
    return [normalizeLectureBlock({ type: 'image', ...item })];
  }
  const heading = item.heading || item.title || item.name || 'Section';
  const text = item.body || item.content || item.text || '';
  const section = emptyLectureSection(String(heading), String(text));
  section.images = attachedImagesFromRaw(item);
  if (typeof item.image === 'string') {
    const extra = normalizeAttachedImage({
      imageUrl: item.image,
      alt: item.imageAlt || item.alt || '',
      description: item.imageDescription || item.description || '',
    });
    if (extra) section.images.push(extra);
  }
  return [section];
}

function applyLooseImages(blocks, images) {
  if (!Array.isArray(images) || !images.length) return blocks;
  const next = [...blocks];
  images.forEach((img) => {
    const block = normalizeLectureBlock({ type: 'image', ...(typeof img === 'object' ? img : { url: img }) });
    const after = typeof img === 'object' ? img.insertAfter || img.after : '';
    if (after) {
      const idx = next.findIndex(
        (b) => b.type === 'section' && String(b.heading || '').toLowerCase() === String(after).toLowerCase()
      );
      if (idx >= 0) {
        next.splice(idx + 1, 0, block);
        return;
      }
    }
    next.push(block);
  });
  return next;
}

export function importLectureJson(raw) {
  let data = raw;
  if (typeof raw === 'string') {
    data = JSON.parse(raw);
  }
  if (data == null) {
    throw new Error('JSON file is empty.');
  }

  let title = '';
  let blocks = [];
  let youtubeVideos = [];

  if (Array.isArray(data)) {
    data.forEach((item) => {
      blocks.push(...sectionFromUnknown(item));
    });
  } else if (typeof data === 'object') {
    if (data.title) title = String(data.title);
    youtubeVideos = normalizeYoutubeVideos(data.youtubeVideos || data.videos || data.youtube || []);
    if (Array.isArray(data.blocks) && data.blocks.length) {
      data.blocks.forEach((item) => {
        blocks.push(...sectionFromUnknown(item));
      });
    } else if (Array.isArray(data.contentBlocks) && data.contentBlocks.length) {
      data.contentBlocks.forEach((item) => {
        blocks.push(...sectionFromUnknown(item));
      });
    } else if (Array.isArray(data.sections)) {
      data.sections.forEach((item) => {
        blocks.push(...sectionFromUnknown(item));
      });
      blocks = applyLooseImages(blocks, data.images);
    } else if (typeof data.content === 'string' && data.content.trim()) {
      blocks = parseAuthoredString(data.content);
      blocks = applyLooseImages(blocks, data.images);
    } else {
      throw new Error(
        'Unrecognized JSON. Use { "title", "sections": [{ "heading", "body" }] }. Upload images on each section in the editor.'
      );
    }
  } else {
    throw new Error('JSON must be an object or an array.');
  }

  blocks = foldLectureBlocks(blocks).filter(blockHasBody);
  if (!blocks.length) {
    throw new Error('No lecture sections found in this JSON file.');
  }

  return { title, blocks, source: 'json', youtubeVideos };
}

export const SAMPLE_LECTURE_JSON = {
  _chatgpt:
    'Text only. After import, upload figures on each section in the lecture editor (multiple files per section). Do not put image URLs in this JSON.',
  title: 'Adding Fractions with the Same Denominator',
  sections: [
    {
      type: 'section',
      heading: 'Introduction',
      body: 'In this lesson you will learn how to add fractions that already share a denominator. This is a common GED Mathematical Reasoning skill.',
    },
    {
      type: 'section',
      heading: 'What You Need to Know',
      body: 'A fraction has a numerator (top) and a denominator (bottom). When denominators are the same, you add the numerators and keep the denominator.',
    },
    {
      type: 'section',
      heading: 'Step-by-Step Method',
      body: 'Step 1: Confirm the denominators are equal.\nStep 2: Add the numerators.\nStep 3: Keep the same denominator.\nStep 4: Simplify if possible.',
    },
    {
      type: 'section',
      heading: 'Worked Example 1',
      body: '$$\\tfrac{1}{5} + \\tfrac{2}{5} = \\tfrac{3}{5}$$\nSame-size pieces are added by counting how many pieces you have in total.',
    },
    {
      type: 'section',
      heading: 'GED Exam Tips',
      body: 'Watch for mixed numbers. Convert them to improper fractions before adding when that makes the arithmetic clearer.',
    },
  ],
  youtubeVideos: [
    {
      title: 'Adding fractions (worked example)',
      url: 'https://www.youtube.com/watch?v=dQw4w9wgGcQ',
    },
  ],
};

export function sampleLectureJsonText() {
  return `${JSON.stringify(SAMPLE_LECTURE_JSON, null, 2)}\n`;
}

export function persistableLectureBlocks(blocks) {
  return foldLectureBlocks(blocks).map((block, i) => ({
    id: block.id || newLectureBlockId(),
    type: 'section',
    heading: String(block.heading || `Section ${i + 1}`),
    text: String(block.text || ''),
    images: sectionImages(block).map((img) => ({
      id: img.id || newLectureBlockId(),
      imageUrl: String(img.imageUrl || img.url || ''),
      url: String(img.imageUrl || img.url || ''),
      imagePath: String(img.imagePath || img.path || ''),
      path: String(img.imagePath || img.path || ''),
      alt: String(img.alt || ''),
      description: String(img.description || ''),
    })),
  }));
}
